import { Form, Formik, Field, useFormikContext } from 'formik';
import { Package } from 'lucide-react';
import { object, string, SchemaOf } from 'yup';
import { useQueryClient } from '@tanstack/react-query';

import { notifySuccess } from '@/portainer/services/notifications';
import { useFIPSMode } from '@/react/portainer/settings/queries/useFIPSMode';
import { useSystemInfo } from '@/react/portainer/system/useSystemInfo';
import { queryKeys as addonQueryKeys } from '@/react/portainer/addons/query-keys';

import { LoadingButton } from '@@/buttons';
import { FormControl } from '@@/form-components/FormControl';
import { Input } from '@@/form-components/Input';
import { isValidUrl } from '@@/form-components/validate-url';
import { Widget } from '@@/Widget';

import { useUpdateSettingsMutation } from '../../queries';
import { Settings } from '../../types';

import { CatalogStatus } from './CatalogStatus';

// Kept in sync with DefaultCatalogURL (api/http/handler/addonproxy/catalog.go)
const DEFAULT_URL =
  'https://raw.githubusercontent.com/portainer/templates/v3/addons/addons-catalog.json';

interface FormValues {
  addonsCatalogUrl: string;
}

function validation(): SchemaOf<FormValues> {
  return object({
    addonsCatalogUrl: string()
      .default('')
      .test(
        'valid-url',
        'Must be a valid URL',
        (value) => !value || isValidUrl(value)
      ),
  });
}

export function AddonsSettingsPanel({ settings }: { settings: Settings }) {
  const mutation = useUpdateSettingsMutation();
  const queryClient = useQueryClient();
  const isFIPSMode = useFIPSMode();
  const { data: systemInfo } = useSystemInfo();

  // Hidden where add-ons cannot run at all: the setting would have no effect.
  if (isFIPSMode || !systemInfo?.addonsAvailable) {
    return null;
  }

  const initialValues: FormValues = {
    addonsCatalogUrl: settings.AddonsCatalogURL,
  };

  return (
    <Widget id="addons-settings">
      <Widget.Title icon={Package} title="Add-on settings" />
      <Widget.Body>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={validation}
          validateOnMount
        >
          <InnerForm isLoading={mutation.isLoading} />
        </Formik>
      </Widget.Body>
    </Widget>
  );

  function handleSubmit(values: FormValues) {
    mutation.mutate(
      { AddonsCatalogURL: values.addonsCatalogUrl },
      {
        onSuccess() {
          notifySuccess('Success', 'Add-on settings updated');
          // Re-read so the status below reflects the URL just saved. The server
          // caches per URL, so a changed one always resolves afresh.
          queryClient.invalidateQueries(addonQueryKeys.list());
        },
      }
    );
  }
}

function InnerForm({ isLoading }: { isLoading: boolean }) {
  const { isValid, errors } = useFormikContext<FormValues>();

  return (
    <Form className="form-horizontal">
      <div className="form-group">
        <div className="col-sm-12 text-muted small">
          <p>
            You can specify the URL to your own add-on catalog here, to pin a
            reviewed catalog or serve one from an internal mirror. Leave it
            empty to use the catalog Portainer publishes.
          </p>
          <p>
            The default value is <a href={DEFAULT_URL}>{DEFAULT_URL}</a>
          </p>
          <p>
            An entry pointing at a registry with a publicly signed certificate
            should set <code>&quot;tlsVerify&quot;: true</code>, so its chart is
            not accepted from an impostor. Charts mirrored to a registry serving
            a self-signed certificate are pulled without that check.
          </p>
        </div>
      </div>

      <FormControl
        label="Catalog URL"
        inputId="addons_catalog_url"
        errors={errors.addonsCatalogUrl}
      >
        <Field
          as={Input}
          id="addons_catalog_url"
          placeholder={DEFAULT_URL}
          data-cy="settings-addonsCatalogUrl"
          name="addonsCatalogUrl"
        />
      </FormControl>

      <div className="form-group">
        <div className="col-sm-12">
          <CatalogStatus />
        </div>
      </div>

      <div className="form-group">
        <div className="col-sm-12">
          <LoadingButton
            isLoading={isLoading}
            disabled={!isValid}
            data-cy="save-addons-settings-button"
            loadingText="Saving..."
            className="!ml-0"
          >
            Save add-on settings
          </LoadingButton>
        </div>
      </div>
    </Form>
  );
}
